import React, {
  ChangeEvent,
  CSSProperties,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import classNames from 'classnames';
import UITextArea from 'Components/UIComponents/UITextArea';
import { useSelectionRef } from 'Hooks/Common';
import { FieldColorNames } from 'Hooks/DocumentFields/useDocumentFieldColor';
import { Signer } from 'Interfaces/Document';
import { DocumentFieldUpdatePayload } from 'Interfaces/DocumentFields';
import Interact from 'Services/Interact';

interface TextFieldProps {
  onFieldDataChange?: (fieldData: Omit<DocumentFieldUpdatePayload, 'id'>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  value?: string | null;
  placeholder?: string;
  style: CSSProperties;
  disabled?: boolean;
  minFontSize?: number;
  maxFontSize?: number;
  signer?: Signer;
  fieldColor?: FieldColorNames;
  isFixedFontSize?: boolean;
}

const TextField = (
  {
    onFieldDataChange,
    onFocus,
    onBlur,
    style,
    value,
    placeholder,
    disabled = false,
    minFontSize = 7,
    maxFontSize = 48,
    fieldColor,
    signer,
    isFixedFontSize = false,
  }: TextFieldProps,
  ref,
) => {
  const signerName = signer?.name || signer?.role;

  const shadowRef = useRef<HTMLPreElement>(null);
  const { captureFromEvent, restoreOnNextTick } = useSelectionRef<HTMLTextAreaElement>();

  const changeTextValue = useCallback(
    async (event: ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = event.target;

      captureFromEvent(event);

      if (onFieldDataChange && shadowRef.current) {
        const prevValue = shadowRef.current.innerHTML;

        shadowRef.current.innerHTML = value;
        const parent = shadowRef.current.parentElement as HTMLElement;

        if (
          isFixedFontSize &&
          (shadowRef.current.offsetWidth > parent.offsetWidth ||
            shadowRef.current.offsetHeight > parent.offsetHeight)
        ) {
          return onFieldDataChange({
            text: value,
          });
        }

        const newFontSize = await Interact.getRearrangedFontSize({
          initialFontSize: style.fontSize as number,
          shadowElement: shadowRef.current,
          minFontSize,
          maxFontSize,
          isFixedFontSize,
        });

        if (newFontSize) {
          shadowRef.current.innerHTML = prevValue;
          shadowRef.current.style.fontSize = style.fontSize + 'px';

          onFieldDataChange({
            text: value,
            fontSize: newFontSize,
            style: {
              fontSize: newFontSize,
            },
          });
        }
      }
      restoreOnNextTick(() => {
        const textarea = ref && 'current' in ref ? ref.current : null;

        return textarea || null;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onFieldDataChange, isFixedFontSize, style.fontSize, minFontSize, maxFontSize],
  );

  const textValue = value || '';
  const formattedValue = (value || 'Textbox').replace(/\n/g, '\n&#8203;');

  return (
    <div
      style={{
        fontFamily: style.fontFamily,
        fontSize: style.fontSize,
      }}
      className={classNames('fieldDropDown__trigger-text', `fieldColor__${fieldColor}`, {
        disabled_pointer_event: disabled,
      })}
    >
      <pre
        ref={shadowRef}
        style={{
          fontFamily: style.fontFamily,
          fontSize: style.fontSize,
        }}
        dangerouslySetInnerHTML={{ __html: formattedValue }}
        className="fieldDropDown__trigger-text-content fieldDropDown__trigger-text-shadow fieldDropDown__trigger-text-content--insertable"
      />
      <UITextArea
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={changeTextValue}
        ref={ref}
        height="100%"
        defaultValue={textValue}
        className="fieldDropDown__trigger-text-content fieldDropDown__trigger-text-input fieldDropDown__trigger-text-input--sign fieldDropDown__trigger-text-content--insertable"
        disabled={disabled}
        placeholder={placeholder || (!disabled ? textValue : signerName)}
      />
    </div>
  );
};

export default forwardRef(TextField);
